import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';

const Navbar = ({ onQuoteClick, onBookingClick }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [industriesDropdownOpen, setIndustriesDropdownOpen] = useState(false);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services', hasDropdown: 'services' },
    { name: 'Industries', path: '/about', hasDropdown: 'industries' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' },
    { name: 'Clients', path: '/clients' },
  ];

  const servicePages = [
    { name: 'Canopy, Duct & Rooftop Fan Cleaning', path: '/services/canopy-duct-fan-cleaning-perth' },
    { name: 'Commercial Kitchen Cleaning', path: '/services/commercial-kitchen-cleaning-perth' },
    { name: 'Warehouse & Industrial Cleaning', path: '/services/warehouse-industrial-cleaning-perth' },
    { name: 'Commercial Office Cleaning', path: '/services/commercial-office-cleaning-perth' },
    { name: 'Commercial Window Cleaning', path: '/services/commercial-window-cleaning-perth' },
    { name: 'Commercial Carpet Cleaning', path: '/services/commercial-carpet-cleaning-perth' },
    { name: 'Commercial High-Pressure Cleaning', path: '/services/commercial-high-pressure-cleaning-perth' },
    { name: 'Commercial Pizza Oven Cleaning', path: '/services/commercial-pizza-oven-cleaning-perth' },
  ];

  const industryPages = [
    { name: 'Schools & Educational Facilities', path: '/industries/schools-educational-facilities-perth' },
    { name: 'Government Buildings', path: '/industries/government-building-cleaning-perth' },
    { name: 'Aged Care & Healthcare Facilities', path: '/industries/aged-care-healthcare-cleaning-perth' },
    { name: 'Strata & Apartment Complexes', path: '/industries/strata-apartment-cleaning-perth' },
  ];

  const isActive = (path) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');
  const isIndustriesActive = () => location.pathname.startsWith('/industries');

  return (
    <nav className="fixed top-0 left-0 right-0 z-[9999] bg-white border-b border-gray-200" data-testid="navbar">
      {/* Container with max-width and equal padding */}
      <div className="w-full max-w-[2560px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 overflow-visible">
        <div className="flex items-center justify-between h-20 lg:h-32 xl:h-36 2xl:h-44 overflow-visible">
          {/* Logo - responsive sizing */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_customer-portal-v2/artifacts/kd8dey33_logo_final-1_1_page-0001.jpeg" 
              alt="Extreme Clean Australia" 
              className="h-14 lg:h-24 xl:h-28 2xl:h-[140px] w-auto"
            />
          </Link>

          {/* Desktop Navigation - hidden below 1100px */}
          <div className="hidden nav-desktop:flex items-center flex-1 justify-center px-2 lg:px-4 xl:px-6 min-w-0 overflow-visible">
            <div className="flex items-center gap-3 lg:gap-4 xl:gap-6 2xl:gap-8 flex-shrink overflow-visible">
              {navLinks.map((link) => (
                link.hasDropdown === 'services' ? (
                  <div 
                    key={link.path + link.name}
                    className="relative flex-shrink-0 overflow-visible"
                    onMouseEnter={() => setServicesDropdownOpen(true)}
                    onMouseLeave={() => setServicesDropdownOpen(false)}
                  >
                    <Link
                      to={link.path}
                      className={`text-xs lg:text-sm font-semibold uppercase tracking-wider transition-colors duration-200 whitespace-nowrap flex items-center gap-1 ${
                        isServicesActive() ? 'text-[#3B82F6]' : 'text-gray-700 hover:text-[#3B82F6]'
                      }`}
                      data-testid={`nav-link-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown size={14} className={`transition-transform ${servicesDropdownOpen ? 'rotate-180' : ''}`} />
                    </Link>
                    
                    {/* Services Dropdown Menu */}
                    {servicesDropdownOpen && (
                      <div className="absolute top-full left-0 pt-2 w-72 z-[10000]">
                        <div className="bg-white shadow-xl border border-gray-200 rounded-sm">
                          {servicePages.map((service) => (
                            <Link
                              key={service.path}
                              to={service.path}
                              className={`block px-4 py-3 text-sm transition-colors ${
                                isActive(service.path) 
                                  ? 'bg-[#3B82F6] text-white' 
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-[#3B82F6]'
                              }`}
                              data-testid={`dropdown-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {service.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : link.hasDropdown === 'industries' ? (
                  <div 
                    key={link.path + link.name}
                    className="relative flex-shrink-0 overflow-visible"
                    onMouseEnter={() => setIndustriesDropdownOpen(true)}
                    onMouseLeave={() => setIndustriesDropdownOpen(false)}
                  >
                    <span
                      className={`text-xs lg:text-sm font-semibold uppercase tracking-wider transition-colors duration-200 whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                        isIndustriesActive() ? 'text-[#3B82F6]' : 'text-gray-700 hover:text-[#3B82F6]'
                      }`}
                      data-testid={`nav-link-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                      <ChevronDown size={14} className={`transition-transform ${industriesDropdownOpen ? 'rotate-180' : ''}`} />
                    </span>
                    
                    {/* Industries Dropdown Menu */}
                    {industriesDropdownOpen && (
                      <div className="absolute top-full left-0 pt-2 w-72 z-[10000]">
                        <div className="bg-white shadow-xl border border-gray-200 rounded-sm">
                          {industryPages.map((industry) => (
                            <Link
                              key={industry.path}
                              to={industry.path}
                              className={`block px-4 py-3 text-sm transition-colors ${
                                isActive(industry.path) 
                                  ? 'bg-[#3B82F6] text-white' 
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-[#3B82F6]'
                              }`}
                              data-testid={`dropdown-${industry.name.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {industry.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-xs lg:text-sm font-semibold uppercase tracking-wider transition-colors duration-200 whitespace-nowrap flex-shrink-0 ${
                      isActive(link.path) ? 'text-[#3B82F6]' : 'text-gray-700 hover:text-[#3B82F6]'
                    }`}
                    data-testid={`nav-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>
          </div>

          {/* Desktop CTA - Phone & Quote Button */}
          <div className="hidden nav-desktop:flex items-center gap-3 lg:gap-4 flex-shrink-0">
            {/* Phone - icon only below 1280px, full number above */}
            <a 
              href="tel:0861875523" 
              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-[#3B82F6] transition-colors"
              data-testid="phone-link"
            >
              <Phone size={18} className="flex-shrink-0" />
              <span className="hidden nav-phone:inline whitespace-nowrap">08 6187 5523</span>
            </a>
            <button
              onClick={onQuoteClick}
              className="bg-[#3B82F6] text-white px-4 lg:px-5 xl:px-6 py-2.5 lg:py-3 text-xs lg:text-sm font-bold uppercase tracking-wider hover:bg-[#273268] transition-colors whitespace-nowrap rounded flex-shrink-0"
              data-testid="get-quote-btn"
            >
              Get Quote
            </button>
          </div>

          {/* Mobile Menu Button - visible below 1100px */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="nav-desktop:hidden p-2 text-[#0A0A0A] ml-auto flex-shrink-0"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - visible below 1100px */}
      {mobileMenuOpen && (
        <div className="nav-desktop:hidden fixed inset-0 top-20 bg-white z-[9998]" data-testid="mobile-menu">
          <div className="flex flex-col p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-5rem)]">
            {navLinks.map((link) => (
              link.hasDropdown === 'services' ? (
                <div key={link.path + link.name}>
                  <Link
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`text-xl font-bold uppercase tracking-widest ${
                      isServicesActive() ? 'text-[#3B82F6]' : 'text-[#0A0A0A]'
                    }`}
                  >
                    {link.name}
                  </Link>
                  <div className="ml-4 mt-3 space-y-3">
                    {servicePages.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block text-base font-medium ${
                          isActive(service.path) ? 'text-[#3B82F6]' : 'text-gray-600'
                        }`}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : link.hasDropdown === 'industries' ? (
                <div key={link.path + link.name}>
                  <span
                    className={`text-xl font-bold uppercase tracking-widest ${
                      isIndustriesActive() ? 'text-[#3B82F6]' : 'text-[#0A0A0A]'
                    }`}
                  >
                    {link.name}
                  </span>
                  <div className="ml-4 mt-3 space-y-3">
                    {industryPages.map((industry) => (
                      <Link
                        key={industry.path}
                        to={industry.path}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`block text-base font-medium ${
                          isActive(industry.path) ? 'text-[#3B82F6]' : 'text-gray-600'
                        }`}
                      >
                        {industry.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-xl font-bold uppercase tracking-widest ${
                    isActive(link.path) ? 'text-[#3B82F6]' : 'text-[#0A0A0A]'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
            <hr className="border-gray-200" />
            <a 
              href="tel:0861875523" 
              className="flex items-center gap-3 text-lg font-medium text-[#0A0A0A]"
            >
              <Phone size={20} />
              <span>08 6187 5523</span>
            </a>
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onQuoteClick();
              }}
              className="bg-[#3B82F6] text-white px-6 py-4 text-sm font-bold uppercase tracking-widest w-full rounded"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
